/* ==========================================================================
   Terlato Kitchen — basket · gift message editor
   sections/gf-cart.liquid

   An upgrade, not a requirement. Without this file the page is still a working
   cart: quantity is plain links to routes.cart_change_url, and the gift editor
   is a real form POSTing to the same route. All this adds is saving the note
   without a page reload, and collapsing the editor behind "Edit message" so a
   basket of four gifts is not four open textareas.

   The editor starts OPEN in the markup and is collapsed here. A disclosure that
   only JavaScript can open would, with JavaScript off, be a button that does
   nothing in front of a form nobody can reach.

   PROPERTIES ARE REPLACED, NOT MERGED. /cart/change.js overwrites a line's
   properties with whatever it is given, so the request below sends every field
   in the form — the note plus the hidden inputs carrying the line's other
   properties. Posting the message alone would drop the "Gift" flag the product
   page set.
   ========================================================================== */

(function (window, document) {
  'use strict';

  var SEL = {
    root: '[data-gf-cart]',
    gift: '[data-gf-cart-gift]',
    toggle: '[data-gf-cart-gift-toggle]',
    form: '[data-gf-cart-gift-form]',
    input: '[data-gf-cart-gift-input]',
    value: '[data-gf-cart-gift-value]',
    status: '[data-gf-cart-gift-status]'
  };

  function each(list, fn) {
    for (var i = 0; i < list.length; i++) fn(list[i], i);
  }

  /* ---------------------------------------------------------------------
     Hiding an app widget by its words

     The GiftShip embed injects into this cart before the checkout button. Most
     of it carries a `gs__` class and the section's CSS hides it by selector —
     but not all of it does, and a selector cannot be written for markup that
     has no distinguishing class.

     So the leftovers are matched on their visible text instead. That is a
     blunt tool, and it is fenced accordingly:

     - the DEEPEST element carrying the phrase is the starting point, so a
       match never begins at a wrapper that also holds half the summary;
     - it then climbs only while the parent says nothing MORE than the phrase,
       which is what promotes "Ship to Multiple Addresses" from its label to
       the row holding the label and its checkbox;
     - it refuses outright to hide anything containing one of this section's
       own `data-gf-*` nodes or the checkout button, so a bad phrase can cost
       a widget but never the ability to check out.
     --------------------------------------------------------------------- */

  function normalise(text) {
    return String(text).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  /* True when hiding `el` would take any of our own markup with it. */
  function ownsSomethingOfOurs(el) {
    if (el.hasAttribute && (el.hasAttribute('data-gf-cart') || el.hasAttribute('data-gf-cart-line'))) return true;
    return !!el.querySelector('[data-gf-cart-gift], [data-gf-cart-line], [name="checkout"], .gf-cart-summary__title');
  }

  function deepestMatch(root, phrase) {
    var found = null;
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.hidden) continue;
      if (normalise(el.textContent).indexOf(phrase) === -1) continue;
      /* Deeper wins: keep the last match that has no matching descendant. */
      var deeper = false;
      var kids = el.querySelectorAll('*');
      for (var k = 0; k < kids.length; k++) {
        if (normalise(kids[k].textContent).indexOf(phrase) !== -1) { deeper = true; break; }
      }
      if (!deeper) found = el;
    }
    return found;
  }

  /* ---------------------------------------------------------------------
     Suppressed app fields must stop participating in the form

     Hiding is only half of it. GiftShip's message box is
     `<textarea name="attributes[Message]" required>`, and a `required` control
     that is not rendered makes the browser refuse to submit the form it sits
     in — "An invalid form control with name='attributes[Message]' is not
     focusable" — with nothing on screen to explain why and nothing a shopper
     can do about it. Checkout simply stops working.

     So every suppressed foreign control is disabled: a disabled control is
     neither validated nor submitted, and it stays in the DOM where the app's
     own script can still read it. Dropping `required` alone would clear the
     error but still post an empty `attributes[Message]` onto the cart.

     Ours are left alone, identified by ancestry rather than by name, so a
     rename here cannot accidentally disable the gift editor or the checkout
     button.
     --------------------------------------------------------------------- */

  var OURS = '[data-gf-cart-gift-form], [data-gf-cart-checkout]';

  function matchesAny(el, selectors) {
    for (var i = 0; i < selectors.length; i++) {
      var sel = selectors[i].trim();
      if (!sel) continue;
      try {
        if (el.closest(sel)) return true;
      } catch (err) {
        /* A selector a merchant typed can be invalid. Skip it rather than
           taking the whole sweep — and the rest of the list — down with it. */
      }
    }
    return false;
  }

  function suppressForeignControls(root, selectors) {
    var controls = root.querySelectorAll('input:not([type="hidden"]), select, textarea');
    each(controls, function (el) {
      if (el.classList.contains('gf-cart-gift__input')) return;
      if (el.closest(OURS) && !matchesAny(el, selectors)) return;
      if (!el.closest('[hidden]') && !matchesAny(el, selectors)) return;

      if (el.required) el.required = false;
      el.disabled = true;
    });
  }

  function hideByText(root, phrases) {
    for (var i = 0; i < phrases.length; i++) {
      var phrase = normalise(phrases[i]);
      if (!phrase) continue;

      var el = deepestMatch(root, phrase);
      if (!el) continue;

      /* Climb while the parent adds no words of its own. */
      while (
        el.parentElement &&
        el.parentElement !== root &&
        normalise(el.parentElement.textContent) === normalise(el.textContent) &&
        !ownsSomethingOfOurs(el.parentElement)
      ) {
        el = el.parentElement;
      }

      if (ownsSomethingOfOurs(el)) continue;
      el.hidden = true;
      el.style.display = 'none';
    }
  }

  function Cart(root) {
    this.root = root;
    this.busy = false;
    this.errorLabel = root.getAttribute('data-gf-error-label') || "That didn't save. Please try again.";
    this.watchAppWidgets();
    if (!window.fetch || !window.FormData) return;
    var self = this;
    each(root.querySelectorAll(SEL.gift), function (gift) { self.wire(gift); });
    each(root.querySelectorAll('[data-gf-cart-qty]'), function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        if (self.busy || self.blockUnsaved()) return;
        var line = link.closest('[data-gf-cart-key]');
        self.change({ id:line.dataset.gfCartKey, quantity:Number(link.dataset.gfCartQty) });
      });
    });
    var checkout = root.querySelector('[data-gf-cart-checkout]');
    if (checkout) checkout.addEventListener('submit', function (event) {
      if (root.dataset.gfPantryBlocked === 'true') {
        event.preventDefault();
        self.message(root.dataset.gfPantryMessage);
        return;
      }
      if (self.busy || self.blockUnsaved()) event.preventDefault();
    });
  }
  Cart.prototype.watchAppWidgets = function () {
    var root = this.root;
    var phrases = (root.getAttribute('data-gf-hide-text') || '').split(',');
    var selectors = (root.getAttribute('data-gf-hide-selectors') || '').split(',');
    var sweep = function () { hideByText(root, phrases); suppressForeignControls(root, selectors); };
    sweep();
    if (!window.MutationObserver) return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () { pending = false; sweep(); }, 50);
    }).observe(root, { childList:true, subtree:true });
  };
  Cart.prototype.message = function (text) {
    var status = this.root.querySelector('[data-gf-cart-status]');
    if (status) { status.textContent = text; status.hidden = !text; }
  };
  Cart.prototype.dirtyForm = function () {
    return Array.from(this.root.querySelectorAll(SEL.form)).find(function (form) {
      var input = form.querySelector(SEL.input);
      return input && input.value !== form.dataset.gfNoteOriginal;
    });
  };
  Cart.prototype.blockUnsaved = function () {
    var form = this.dirtyForm();
    if (!form) return false;
    this.message('Save your gift message before continuing.');
    form.hidden = false;
    form.querySelector(SEL.input).disabled = false;
    form.closest(SEL.gift).querySelector(SEL.toggle).setAttribute('aria-expanded', 'true');
    form.querySelector(SEL.input).focus();
    return true;
  };
  Cart.prototype.wire = function (gift) {
    var self = this;
    var toggle = gift.querySelector(SEL.toggle);
    var form = gift.querySelector(SEL.form);
    if (!toggle || !form) return;
    toggle.hidden = false;
    form.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.addEventListener('click', function () {
      if (self.busy) return;
      var dirty = self.dirtyForm();
      if (dirty && dirty !== form) { self.blockUnsaved(); return; }
      if (form.hidden) {
        each(self.root.querySelectorAll(SEL.form), function (other) {
          if (other !== form) { other.hidden = true; other.closest(SEL.gift).querySelector(SEL.toggle).setAttribute('aria-expanded', 'false'); }
        });
        form.hidden = false;
        form.querySelector(SEL.input).disabled = false;
        toggle.setAttribute('aria-expanded', 'true');
        form.querySelector(SEL.input).focus();
      } else {
        form.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      }
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (self.busy || !form.reportValidity()) return;
      var fields = new FormData(form);
      var properties = {};
      fields.forEach(function (value, key) {
        var match = /^properties\[(.*)\]$/.exec(key);
        if (match) properties[match[1]] = value;
      });
      self.change({ id:fields.get('id'), quantity:Number(fields.get('quantity')), properties:properties });
    });
  };
  Cart.prototype.setBusy = function (busy) {
    this.busy = busy;
    this.root.setAttribute('aria-busy', String(busy));
    each(this.root.querySelectorAll('button, textarea'), function (control) {
      if (busy) { control.dataset.gfPreviouslyDisabled = String(control.disabled); control.disabled = true; }
      else { control.disabled = control.dataset.gfPreviouslyDisabled === 'true'; delete control.dataset.gfPreviouslyDisabled; }
    });
  };
  Cart.prototype.change = function (payload) {
    var self = this;
    this.setBusy(true);
    this.message('Updating your basket…');
    var routesRoot = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    fetch(routesRoot + 'cart/change.js', {
      method:'POST', headers:{ 'Content-Type':'application/json', Accept:'application/json' }, body:JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (cart) {
        if (!response.ok || cart.status) throw new Error(cart.description || cart.message || self.errorLabel);
        return cart;
      });
    }).then(function () {
      // Properties can change a line key or merge matching lines. Re-render from
      // Shopify after every mutation so subsequent edits use current keys/totals.
      window.location.reload();
    }).catch(function (problem) {
      self.setBusy(false);
      self.message(problem.message || self.errorLabel);
    });
  };
  function init(scope) {
    each((scope || document).querySelectorAll(SEL.root), function (root) {
      if (root.gfCart) return;
      root.gfCart = new Cart(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); }); else init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})(window, document);
